"use client";
import image from "@/constants/image";
import { APP_ROUTES } from "@/constants/routes";
import { TYPE_FAVORITE } from "@/models/report";
import ReportServices from "@/services/main/reportServices";
import CloseIcon from "@mui/icons-material/Close";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import {
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import { Montserrat } from "next/font/google";
import Image from "next/image";
import { redirect } from "next/navigation";
import { useEffect, useState } from "react";

interface ModalUpcomingProps {
  handleClose: () => void;
  open: boolean;
  isLoggedIn?: boolean;
  dataModalUpcoming?: any;
  queryKeyUpcoming: (string | number | boolean | null | undefined)[];
  locale?: string;
}
const montserrat = Montserrat({
  subsets: ["latin"],
});

const ModalUpcoming = (props: ModalUpcomingProps) => {
  const {
    open,
    handleClose,
    isLoggedIn,
    dataModalUpcoming,
    queryKeyUpcoming,
    locale,
  } = props;
  const t = useTranslations("messages");
  const queryClient = useQueryClient();
  const [isFavorite, setIsFavorite] = useState<boolean>(
    dataModalUpcoming?.isFavorite || false
  );
  const [currentCount, setCurrentCount] = useState<number>(
    dataModalUpcoming?.favoritesCount || 0
  );
  useEffect(() => {
    setIsFavorite(!!dataModalUpcoming?.isFavorite);
    setCurrentCount(dataModalUpcoming?.favoritesCount || 0);
  }, [dataModalUpcoming]);

  const AddFavorite = useMutation({
    mutationFn: async (reportId: number) => {
      const result = await ReportServices.postAddFavorite(
        reportId,
        TYPE_FAVORITE.FAVORITE
      );
      return result;
    },
  });

  const handleFavorite = async (
    id: number,
    currentFavorites: boolean,
    currentCount: number
  ) => {
    if (!isLoggedIn) {
      const currentPath =
        typeof window !== "undefined"
          ? window.location.pathname +
            window.location.search +
            window.location.hash
          : "";
      redirect(
        `${APP_ROUTES(locale as any).LOGIN.to}?redirect=${encodeURIComponent(currentPath)}`
      );
      return;
    }

    const isCurrentlyFavorited = currentFavorites;
    const newFavorites = isCurrentlyFavorited ? false : true;
    const newCount = isCurrentlyFavorited
      ? Number(currentCount) - 1
      : Number(currentCount) + 1;
    setIsFavorite(newFavorites);
    setCurrentCount(newCount);
    queryClient.setQueryData(queryKeyUpcoming, (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          data: page.data.map((item: any) =>
            item.id === id
              ? {
                  ...item,
                  isFavorite: newFavorites,
                  favoritesCount: newCount,
                }
              : item
          ),
        })),
      };
    });
    try {
      await AddFavorite.mutateAsync(id);
    } catch {
      setIsFavorite(currentFavorites);
      setCurrentCount(currentCount);
      queryClient.setQueryData(queryKeyUpcoming, (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            data: page.data.map((item: any) =>
              item.id === id
                ? {
                    ...item,
                    isFavorite: currentFavorites,
                    favoritesCount: currentCount,
                  }
                : item
            ),
          })),
        };
      });
    }
  };

  return (
    <Dialog
      maxWidth="md"
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle className={montserrat.className}>
        <Box className="flex justify-between items-center">
          <Box className="flex items-center flex-1">
            <Box className="h-8 w-8 flex items-center justify-center">
              <Image
                src={image.iconCampaign2}
                alt="iconCampaign"
                width={25.54}
                height={19.23}
              />
            </Box>
            <h4
              className="text-gray_60 font-semibold text-lg/6 m-0"
              translation-key="home_report_coming_soon"
            >
              {t("home_report_coming_soon")}
            </h4>
          </Box>
          <Box>
            <IconButton
              className="!bg-eerie_black_5 size-8 p-1"
              onClick={handleClose}
            >
              <CloseIcon className="text-[rgba(28, 28, 28, 0.4)]" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={`${!isLoggedIn && "mb-8"} !pb-6`}>
        <Box className="flex items-start gap-2">
          <DialogContentText
            className={`!my-1 !tracking-[-3%] !text-2xl/8 !font-semibold !text-ciBlue_900`}
            style={{ wordBreak: "break-word" }}
          >
            {dataModalUpcoming?.title}
          </DialogContentText>
          {isLoggedIn && (
            <Tooltip
              title={
                <span
                  className={`${montserrat.className}`} // Đảm bảo biến montserrat có sẵn
                  translation-key={
                    isFavorite ? "common_favorited" : "common_favorite"
                  }
                >
                  {isFavorite ? t("common_favorited") : t("common_favorite")}
                </span>
              }
              disableInteractive
              placement="bottom"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -14], // Điều chỉnh vị trí của tooltip
                      },
                    },
                  ],
                },
              }}
            >
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="!my-1 border-0 py-1 px-1 bg-white hover:bg-white cursor-pointer rounded flex justify-center items-center"
                onClick={() =>
                  handleFavorite(
                    dataModalUpcoming?.id,
                    isFavorite,
                    currentCount
                  )
                }
                aria-label={
                  isFavorite ? t("common_favorited") : t("common_favorite")
                }
              >
                {isFavorite ? (
                  <FavoriteIcon className="h-6 w-6 text-warning" />
                ) : (
                  <FavoriteBorderIcon className="h-6 w-6 text-warning" />
                )}
              </motion.button>
            </Tooltip>
          )}
        </Box>
        <Box>
          <Box
            dangerouslySetInnerHTML={{ __html: dataModalUpcoming?.description }}
          ></Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ModalUpcoming;
